/**
 * useDemandOverrides Hook
 *
 * Manages demand overrides — manual adjustments to AI-generated demand
 * forecasts for specific strain family/week combinations. Overrides take
 * precedence over forecasted demand in the production planner.
 *
 * US-C2-008: Demand Override & Manual Adjustments
 * Strain Families: overrides key on strain_family_id instead of strain_id
 */

import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/supabase-client'
import { toast } from 'sonner'

/**
 * Hook to fetch and manage demand overrides
 *
 * @param {Object} options - Configuration options
 * @param {boolean} [options.enabled=true] - Whether to fetch data
 * @returns {Object} { overrides, loading, error, setOverride, removeOverride, getOverrideForWeek, refetch }
 */
export function useDemandOverrides({ enabled = true } = {}) {
  const [overrides, setOverrides] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // -------------------------------------------------------------------------
  // Fetch all overrides
  // -------------------------------------------------------------------------
  const fetchOverrides = useCallback(async () => {
    if (!enabled) {
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const { data, error: fetchError } = await supabase
        .from('demand_overrides')
        .select(`
          id,
          strain_family_id,
          week_start,
          override_kg,
          reason,
          created_by,
          created_at
        `)
        .order('week_start', { ascending: true })

      if (fetchError) throw fetchError

      setOverrides(data || [])
    } catch (err) {
      console.error('[useDemandOverrides] Fetch error:', err)
      setError(err.message || 'Failed to fetch demand overrides')
      setOverrides([])
    } finally {
      setLoading(false)
    }
  }, [enabled])

  useEffect(() => {
    fetchOverrides()
  }, [fetchOverrides])

  // -------------------------------------------------------------------------
  // Upsert an override (create or update)
  // -------------------------------------------------------------------------
  const setOverride = useCallback(async (familyId, weekStart, overrideKg, reason = null) => {
    try {
      const { data, error: upsertError } = await supabase
        .from('demand_overrides')
        .upsert(
          {
            strain_family_id: familyId,
            week_start: weekStart,
            override_kg: overrideKg,
            reason
          },
          { onConflict: 'strain_family_id,week_start' }
        )
        .select()
        .single()

      if (upsertError) throw upsertError

      // Update local cache
      setOverrides(prev => {
        const idx = prev.findIndex(
          o => o.strain_family_id === familyId && o.week_start === weekStart
        )
        if (idx >= 0) {
          const updated = [...prev]
          updated[idx] = data
          return updated
        }
        return [...prev, data]
      })

      toast.success('Demand override saved')
      return data
    } catch (err) {
      console.error('[useDemandOverrides] Upsert error:', err)
      toast.error('Failed to save demand override')
      throw err
    }
  }, [])

  // -------------------------------------------------------------------------
  // Remove an override
  // -------------------------------------------------------------------------
  const removeOverride = useCallback(async (familyId, weekStart) => {
    try {
      const { error: deleteError } = await supabase
        .from('demand_overrides')
        .delete()
        .eq('strain_family_id', familyId)
        .eq('week_start', weekStart)

      if (deleteError) throw deleteError

      // Update local cache
      setOverrides(prev =>
        prev.filter(o => !(o.strain_family_id === familyId && o.week_start === weekStart))
      )

      toast.success('Demand override removed')
    } catch (err) {
      console.error('[useDemandOverrides] Delete error:', err)
      toast.error('Failed to remove demand override')
      throw err
    }
  }, [])

  // -------------------------------------------------------------------------
  // Lookup override from local cache
  // -------------------------------------------------------------------------
  const getOverrideForWeek = useCallback((familyId, weekStart) => {
    return overrides.find(
      o => o.strain_family_id === familyId && o.week_start === weekStart
    ) || null
  }, [overrides])

  return {
    overrides,
    loading,
    error,
    setOverride,
    removeOverride,
    getOverrideForWeek,
    refetch: fetchOverrides
  }
}

export default useDemandOverrides
